import { useSlots as _useSlots, useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass, mergeProps as _mergeProps, vShow as _vShow, isMemoSame as _isMemoSame } from "vue";
const _hoisted_1 = {
    class: "transcript-layout",
    "aria-labelledby": "transcript-heading"
};
const _hoisted_2 = {
    class: "sr-only",
    "aria-live": "polite",
    "aria-atomic": "true"
};
const _hoisted_3 = { class: "transcript-layout__header" };
const _hoisted_4 = { id: "transcript-heading" };
const _hoisted_5 = {
    key: 0,
    class: "transcript-layout__filters"
};
const _hoisted_6 = ["textContent"];
const _hoisted_7 = ["id", "value"];
const _hoisted_8 = ["for", "textContent"];
const _hoisted_9 = ["innerHTML"];
const _hoisted_10 = {
    key: 1,
    class: "transcript-layout__interleaved-group"
};
import { ref, computed, inject, nextTick, watch } from "vue";
import { storeToRefs } from "pinia";
import { useEventListener } from "@vueuse/core";
import { useLoadAndScroll } from "@/common/composables/scroll-to-target";
import { useProgressiveReveal } from "@/common/composables/progressive-reveal";
import { findFlatSegmentIndexByCitation, VERBATIM_PARAM } from "@/common/helpers/transcript-citation";
import { useTranscriptAutoScroll } from "@/common/composables/transcript-auto-scroll";
import { useTranscriptPlayhead } from "@/common/composables/transcript-playhead";
import { getParticipantColor, getSpeakerColor } from "@/common/helpers/speaker-colors";
import { useMediaPlayerContext } from "@/common/injection-keys/media-player-context";
import { initTranscriptWordElementsContext } from "@/common/injection-keys/transcript-word-elements";
import { initAnnouncerContext } from "@/common/composables/announcer";
import { useTranscriptionsStore } from "@/store/transcriptions";
import { useContext } from "@/store/context";
// Components
import SearchInput from "@/common/components/SearchInput.vue";
import DropdownMenu from "@/common/components/DropdownMenu.vue";
import SvgIcon from "@/common/components/SvgIcon.vue";
import TranscriptMessage from "@/common/components/transcription-layout/TranscriptMessage.vue";
import AlertMessage from "@/common/components/AlertMessage.vue";
import BackToTopButton from "@/common/components/BackToTopButton.vue";
// Types & Enums
import { isChatSourced, transcriptIndexOf } from "@/common/helpers/transcript";
export default /*@__PURE__*/ _defineComponent({
    __name: 'TranscriptLayout',
    props: /*@__PURE__*/ _mergeModels({
        getDisplayName: { type: Function, default: (name) => name ?? "" },
        isSpeakerHost: { type: Function, default: (name) => !!name?.toLowerCase().startsWith("host") },
        isKnownParticipant: { type: Function, default: () => true },
        isSpeakerInactive: { type: Function, default: () => false },
        hideSpeakerFilter: { type: Boolean },
        defaultSpeakerLabel: {},
        speakerFilterPlaceholder: {},
        editable: { type: Boolean, default: false },
        disableSpeakerMenuOptions: { type: Boolean, default: false },
        isHtmlTranscript: { type: Boolean, default: false },
        regenerating: { type: Boolean, default: false },
        interleavedRows: {},
        bannerCopy: {},
        segmentTextAttrs: { type: Function },
        showChatAffordances: { type: Boolean, default: false },
        participants: {},
        regeneratingChannels: {},
        messageFilter: { type: Function },
        disableTextHover: { type: Boolean, default: false },
        disableWordHoverUnderline: { type: Boolean, default: false },
        focusAreaKey: { type: Function },
        colorSource: {},
        colorIdentity: { type: Function }
    }, {
        "selectedSpeakers": { default: () => [] },
        "selectedSpeakersModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["seek", "stream-rendered", "edit-segment", "reveal"], ["update:selectedSpeakers"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        const emit = __emit;
        // Declared so the generic #interleaved-row hands consumers their full row shape
        // back (not just the sortKey axis). The other slots are listed to keep the
        // contract exhaustive; the menu slots are forwarded straight from TranscriptMessage.
        const slots = _useSlots();
        const i18n = inject("i18n");
        const { transcription, transcriptionError, isSelectedEngineAnalyzing, isSelectedEngineFailed, isGeneratingTranscript, isFetchingTranscript } = storeToRefs(useTranscriptionsStore());
        const { mediaPlayerCurrentTime: currentTime, mediaPlayerIsPlaying: isPlaying, mediaPlayerSeekTick: seekTick } = useMediaPlayerContext();
        const { isUnrestricted } = storeToRefs(useContext());
        // Filter & search state. The speaker filter is internal by default; a parent may
        // control it via `v-model:selectedSpeakers` (e.g. a per-message "Filter by
        // participant" action). defineModel gives both modes: a local ref when unbound,
        // two-way sync when bound.
        const selectedSpeakers = _useModel(__props, "selectedSpeakers");
        const searchQuery = ref("");
        // Unique speakers from transcript data
        const speakerOptions = computed(() => {
            if (!transcription.value)
                return [];
            const allSegments = transcription.value.blocks.flatMap((block) => block.segments);
            const speakers = new Set();
            for (const segment of allSegments) {
                if (segment.speaker)
                    speakers.add(segment.speaker);
            }
            return [
                {
                    options: Array.from(speakers).map((speaker) => ({
                        value: speaker,
                        label: speaker
                    }))
                }
            ];
        });
        const speakerFilterOptions = computed(() => speakerOptions.value[0]?.options ?? []);
        // Trigger label: the selected speakers' names (in transcript order), or
        // "Everyone" when nothing is selected (the filter is showing every speaker).
        const selectedSummary = computed(() => {
            const selected = speakerFilterOptions.value.filter((opt) => selectedSpeakers.value.includes(opt.value));
            if (__props.speakerFilterPlaceholder) {
                if (selected.length === 0)
                    return __props.speakerFilterPlaceholder;
                if (selected.length === 1)
                    return __props.getDisplayName(selected[0].label);
                return i18n.t("{0} selected", [selected.length]);
            }
            if (selected.length === 0)
                return i18n.t("Everyone");
            return selected.map((opt) => __props.getDisplayName(opt.label)).join(", ");
        });
        // Flatten segments and group by speaker
        const allMessages = computed(() => {
            if (!transcription.value)
                return [];
            const allSegments = transcription.value.blocks.flatMap((block) => block.segments);
            const messages = [];
            for (const segment of allSegments) {
                const lastMsg = messages[messages.length - 1];
                const seg = { ...segment, words: [...segment.words] };
                const speaker = segment.speaker ?? __props.defaultSpeakerLabel;
                // Group consecutive same-speaker segments, but never merge across `source`
                // (a typed backroom/chat message must not fold into a spoken bubble). EC
                // segments have undefined source so the key is unchanged → identical grouping.
                // Typed messages never group at all: each is its own excerpt unit, so it gets its own card.
                if (lastMsg &&
                    !isChatSourced(segment) &&
                    lastMsg.speaker === speaker &&
                    lastMsg.source === segment.source &&
                    lastMsg.channelLabel === segment.channelLabel) {
                    lastMsg.segments.push(seg);
                }
                else {
                    messages.push({
                        speaker,
                        segments: [seg],
                        startTime: segment.start,
                        isHost: __props.isSpeakerHost(speaker),
                        source: segment.source,
                        channelLabel: segment.channelLabel,
                        isBackroom: segment.isBackroom
                    });
                }
            }
            return messages;
        });
        // Every message stays mounted for the life of the page and carries a `visible` flag the
        // template applies as `v-show` — matching the legacy stream, whose excerpt layer resolves
        // messages by DOM id and measures offsetTop, so filtered rows must stay in the document.
        const streamMessages = computed(() => {
            const query = searchQuery.value.toLowerCase();
            const speakers = selectedSpeakers.value;
            return allMessages.value.map((message) => {
                // Optional consumer predicate (e.g. IDI message-type filter). EC omits it.
                if (__props.messageFilter && !__props.messageFilter(message))
                    return { message, visible: false };
                if (speakers.length > 0 && (message.speaker === undefined || !speakers.includes(message.speaker))) {
                    return { message, visible: false };
                }
                if (!query)
                    return { message, visible: true };
                const segments = message.segments.filter((seg) => seg.text.toLowerCase().includes(query));
                if (segments.length === 0)
                    return { message, visible: false };
                return { message: segments.length === message.segments.length ? message : { ...message, segments }, visible: true };
            });
        });
        // Visible-only. The playhead, auto-scroll and word registry must never resolve to a
        // hidden message.
        const chatMessages = computed(() => streamMessages.value.filter((entry) => entry.visible).map((entry) => entry.message));
        // The rail measures the rendered rows, so the visible set is the trigger — post-flush so the
        // DOM has settled by the time the host re-measures.
        watch(chatMessages, () => emit("stream-rendered"), { flush: "post" });
        // One ordered stream of messages + interleaved rows. Messages sort on startTime,
        // rows on sortKey (same axis). A stable index breaks ties so the original order
        // of equal-keyed entries is preserved (messages keep their relative order). When
        // no interleavedRows are supplied the result is exactly streamMessages, tagged —
        // so EC, which passes none, renders identically.
        const sortKeyOf = (entry) => entry.type === "message" ? entry.message.startTime : entry.row.sortKey;
        const mergedStream = computed(() => {
            const messages = streamMessages.value.map((entry) => ({ type: "message", ...entry }));
            const rows = (__props.interleavedRows ?? []).map((row) => ({ type: "row", row }));
            // Array.sort is stable, so equal sortKeys keep insertion order — a message and a
            // row at the same timestamp preserve message-before-row ordering, no tiebreaker needed.
            return [...messages, ...rows].sort((left, right) => sortKeyOf(left) - sortKeyOf(right));
        });
        // Consecutive interleaved rows collapse into one group so the container owns the
        // tight spacing of a run (a positive gap) rather than a leaf negative margin.
        const displayStream = computed(() => {
            const entries = [];
            // A hidden message generates no box, so it must not split a run of consecutive
            // rows — hence the explicit open group rather than reading entries.at(-1).
            let openRowGroup = null;
            for (const entry of mergedStream.value) {
                if (entry.type === "row") {
                    if (openRowGroup) {
                        openRowGroup.rows.push(entry.row);
                    }
                    else {
                        openRowGroup = { type: "row-group", rows: [entry.row] };
                        entries.push(openRowGroup);
                    }
                }
                else {
                    entries.push({ type: "message", message: entry.message, visible: entry.visible });
                    if (entry.visible)
                        openRowGroup = null;
                }
            }
            return entries;
        });
        // A message key must survive same-speaker/same-startTime collisions (IDI spoken vs
        // backroom, or per-channel bubbles), so source + channelLabel disambiguate.
        const displayKey = (entry, index) => entry.type === "message"
            ? `msg-${entry.message.startTime}-${entry.message.speaker}-${entry.message.source ?? ""}-${entry.message.channelLabel ?? ""}`
            : `rowgroup-${entry.rows[0].sortKey}-${index}`;
        // Weight is counted in segments, not entries: allMessages groups consecutive same-speaker segments,
        // so a transcript without speaker labels collapses to ONE entry and an entry-counted budget would
        // still render everything.
        const { visibleItems, tailCap, isComplete, offsetAt, revealThrough, revealAll } = useProgressiveReveal(() => displayStream.value, {
            weightOf: (entry) => (entry.type === "message" ? entry.message.segments.length : 1),
            keyOf: displayKey,
            enabled: () => !__props.isHtmlTranscript
        });
        // segmentId → its position in the reveal's weight space; an entry weighs one unit per segment.
        const segmentOffsets = computed(() => {
            const offsets = new Map();
            displayStream.value.forEach((entry, index) => {
                if (entry.type !== "message")
                    return;
                const start = offsetAt(index);
                entry.message.segments.forEach((segment, segmentIndex) => offsets.set(segment.id, start + segmentIndex));
            });
            return offsets;
        });
        // A linked participant is the same person everywhere; only an unattributed label is scoped to its recording.
        const speakerColor = (label) => {
            const identity = __props.colorIdentity?.(label);
            return identity == null ? getSpeakerColor(label, __props.colorSource) : getParticipantColor(identity);
        };
        // Only the last revealed entry can be partially drawn, so the cap applies to it alone.
        const segmentEndFor = (index) => index === visibleItems.value.length - 1 ? tailCap.value : undefined;
        // Every input the message row actually binds. The fill only re-slices `visibleItems`, so entry
        // objects stay referentially stable and already-mounted messages skip re-render; a freshly pushed
        // transcript replaces them all and invalidates every key, by design. Without this the per-frame
        // cost grows with what is already rendered, making finer chunks slower overall.
        // Anything added to the row's bindings MUST be added here or it will silently stop updating.
        const memoKeyFor = (entry, index) => {
            const message = entry.type === "message" ? entry.message : null;
            return [
                entry,
                message,
                entry.type === "message" && entry.visible,
                segmentEndFor(index),
                entry === targetEntry.value,
                isEmphasized.value,
                message === null ? false : isMessageRegenerating(message.speaker),
                message === null ? "" : __props.getDisplayName(message.speaker),
                message === null ? "" : speakerColor(message.speaker),
                __props.participants,
                __props.editable,
                __props.disableSpeakerMenuOptions,
                __props.disableTextHover,
                __props.disableWordHoverUnderline,
                __props.showChatAffordances,
                message === null ? false : __props.isKnownParticipant(message.speaker),
                message === null ? false : __props.isSpeakerInactive(message.speaker),
                hasSpeakerMenu(),
                hasSegmentMenu(),
                __props.segmentTextAttrs,
                __props.regeneratingChannels
            ];
        };
        // The host re-arms legacy decorations off this; the excerpt rail reads the DOM, so it needs the
        // fill to have settled rather than a signal per frame.
        watch(isComplete, (complete) => {
            if (complete)
                void nextTick(() => emit("reveal"));
        });
        // Best-effort for a browser-initiated print (Ctrl+P), whose empty call stack lets the patch land in time.
        useEventListener(window, "beforeprint", revealAll);
        // An in-app print call blocks, so a parent must be able to await the fill before it opens the dialog.
        const revealAllAndWaitForRender = () => {
            revealAll();
            return nextTick();
        };
        __expose({ revealAllAndWaitForRender });
        // Flat, start-ordered segment ranges. The playhead composable parks the live
        // --current-time on the active segment's element so only that segment's words
        // restyle each frame — vs every word when a single-speaker transcript is one message.
        const flatSegments = computed(() => chatMessages.value.flatMap((message) => message.segments.map((segment) => ({ id: segment.id, start: segment.start, end: segment.end }))));
        // Resolved by the playhead composable (via data-segment-id) to park --current-time.
        const messagesContainer = ref(null);
        useTranscriptPlayhead({ currentTime, segments: flatSegments, container: messagesContainer });
        // Derived from filtered messages: playback through filtered-out speakers silently no-ops auto-scroll.
        const flatWords = computed(() => {
            const words = [];
            for (const message of chatMessages.value) {
                for (const segment of message.segments) {
                    for (let index = 0; index < segment.words.length; index++) {
                        const word = segment.words[index];
                        words.push({ start: word.start, end: word.end, id: `${segment.id}:${index}` });
                    }
                }
            }
            return words;
        });
        const { wordElements } = initTranscriptWordElementsContext();
        // One shared polite live region for the whole layout; descendants announce via useAnnouncer().
        const { message: announcement } = initAnnouncerContext();
        const { resume: resumeAutoScroll } = useTranscriptAutoScroll({
            currentTime,
            isPlaying,
            flatWords,
            wordElements,
            seekTick,
            // A scrub can land far past the rendered runs; reveal that position so the retry finds the word.
            onWordUnrendered: (wordId) => {
                const offset = segmentOffsets.value.get(Number(wordId.split(":")[0]));
                if (offset !== undefined)
                    revealThrough(offset);
            }
        });
        // Click-on-word (TranscriptMessage emits `seek` for single-click) is an
        // explicit re-engagement: re-arm auto-scroll before the parent applies the seek.
        const onSeek = (timeMs) => {
            resumeAutoScroll();
            emit("seek", timeMs);
        };
        // Deep-link params: read once (URL is stable for the page).
        const deepLinkParams = new URLSearchParams(window.location.search);
        const segmentParam = deepLinkParams.get("segment");
        const verbatimParam = deepLinkParams.get(VERBATIM_PARAM);
        const messageIdParam = parseInt(deepLinkParams.get("message_id") ?? "", 10);
        const transcriptIdParam = parseInt(deepLinkParams.get("transcript_id") ?? "", 10);
        const allSegments = computed(() => allMessages.value.flatMap((msg) => msg.segments));
        // `?segment=N` is an explicit flat index; `?verbatim=<quote>` matches an Ask-AI citation to the same flat index.
        // Reactive because allMessages populates asynchronously after load; segmentParam/verbatimParam are read once at setup.
        const targetSegmentIndex = computed(() => {
            if (segmentParam !== null) {
                const parsed = parseInt(segmentParam, 10);
                return isNaN(parsed) ? null : parsed;
            }
            if (verbatimParam) {
                return findFlatSegmentIndexByCitation(verbatimParam, allSegments.value.map((seg) => seg.text));
            }
            return null;
        });
        // Matched per-segment, not by the row's startTime: a text chat has no media clock
        const targetSegmentMatch = computed(() => {
            if (messageIdParam > 0)
                return (segment) => isChatSourced(segment) && segment.id === messageIdParam;
            if (transcriptIdParam >= 0)
                return (segment) => !isChatSourced(segment) && transcriptIndexOf(segment) === transcriptIdParam;
            if (targetSegmentIndex.value === null)
                return null;
            const target = allSegments.value[targetSegmentIndex.value];
            return target === undefined ? null : (segment) => segment === target;
        });
        const { setTarget, isEmphasized } = useLoadAndScroll();
        const targetEntry = computed(() => {
            const match = targetSegmentMatch.value;
            if (match === null)
                return null;
            const found = displayStream.value.find((item) => item.type === "message" && item.message.segments.some(match));
            return found?.type === "message" ? found : null;
        });
        // Must land before the message's :ref fires — useLoadAndScroll only scrolls to the first target it
        // is handed, so a target still inside a gap would never scroll.
        watch([targetEntry, segmentOffsets], () => {
            // Hidden rows still count toward the offset, so revealing to one mounts a prefix for nothing.
            if (!targetEntry.value?.visible)
                return;
            const firstSegmentId = targetEntry.value.message.segments[0]?.id;
            if (firstSegmentId === undefined)
                return;
            const offset = segmentOffsets.value.get(firstSegmentId);
            if (offset !== undefined)
                revealThrough(offset);
        }, { immediate: true, flush: "post" });
        // A hidden message can't be scrolled to, so filtering the target out clears the target.
        const setMessageTarget = (el) => setTarget(targetEntry.value?.visible ? el : null);
        // Read per render, never cached: `slots` is mutated in place, so a computed would never see a slot appear.
        const hasSpeakerMenu = () => !!slots["speaker-menu"];
        const hasSegmentMenu = () => !!slots["segment-menu"];
        // A stable handler identity keeps a parent render from re-rendering every message.
        const onEditSegmentText = (...args) => emit("edit-segment", ...args);
        // The analysis phase leaves the transcript intact — suppress alt-states so
        // we keep rendering the cached transcript instead of flashing a shimmer.
        // `regenerating` is the content-status fallback for callers without per-engine
        // state (non-SA re-transcribe), where the store getters can't detect the re-run.
        const isAltState = computed(() => {
            if (isSelectedEngineAnalyzing.value)
                return false;
            return (!transcription.value ||
                __props.regenerating ||
                isGeneratingTranscript.value ||
                isFetchingTranscript.value ||
                isSelectedEngineFailed.value ||
                !!transcriptionError.value);
        });
        const regeneratingChannelSet = computed(() => new Set(__props.regeneratingChannels ?? []));
        const isMessageRegenerating = (speaker) => {
            const channel = __props.participants?.[speaker ?? ""]?.channel;
            return channel !== undefined && regeneratingChannelSet.value.has(channel);
        };
        const alertMsg = computed(() => {
            if (__props.bannerCopy)
                return __props.editable ? __props.bannerCopy.editable : __props.bannerCopy.readOnly;
            if (__props.editable) {
                return i18n.t("{0}Click{1} a word to play from that point in the recording, or {0}double-click{1} a word to edit it.", ["<strong>", "</strong>"]);
            }
            return i18n.t("{0}Click{1} a word to play from that point in the recording.", ["<strong>", "</strong>"]);
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("section", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(announcement)), 1),
                _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("h2", _hoisted_4, _toDisplayString(_ctx.$t("Transcript")), 1),
                    (_unref(isUnrestricted))
                        ? _renderSlot(_ctx.$slots, "engine-selector", {}, undefined, undefined, 0)
                        : _createCommentVNode("", true),
                    ((_unref(isUnrestricted) || __props.regenerating) && isAltState.value)
                        ? _renderSlot(_ctx.$slots, "alt-states", {
                            generating: _unref(isGeneratingTranscript) || __props.regenerating,
                            fetching: _unref(isFetchingTranscript),
                            error: _unref(transcriptionError)
                        }, undefined, undefined, 1)
                        : (_openBlock(), _createElementBlock("div", {
                            key: 2,
                            class: _normalizeClass(["transcript-layout__toolbar", { 'transcript-layout__toolbar--paired-filters': !!_ctx.$slots['search-filter'] }])
                        }, [
                            _createVNode(SearchInput, {
                                modelValue: searchQuery.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((searchQuery).value = $event)),
                                placeholder: _ctx.$t('Search transcript...')
                            }, null, 8, ["modelValue", "placeholder"]),
                            (!__props.hideSpeakerFilter || _ctx.$slots['search-filter'])
                                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                    _renderSlot(_ctx.$slots, "search-filter"),
                                    (!__props.hideSpeakerFilter)
                                        ? (_openBlock(), _createBlock(DropdownMenu, {
                                            key: 0,
                                            type: "select",
                                            "auto-close": false
                                        }, {
                                            button: _withCtx(() => [
                                                _createElementVNode("span", {
                                                    class: "transcript-layout__filter-label",
                                                    textContent: _toDisplayString(selectedSummary.value)
                                                }, null, 8, _hoisted_6),
                                                _createVNode(SvgIcon, {
                                                    icon: "icon-arrow-down",
                                                    class: "rec-dropdown__chevron"
                                                })
                                            ]),
                                            menu: _withCtx(() => [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(speakerFilterOptions.value, (opt) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                        key: opt.value,
                                                        class: "rec-dropdown-menu__item"
                                                    }, [
                                                        _withDirectives(_createElementVNode("input", {
                                                            id: `speaker-${opt.value}`,
                                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((selectedSpeakers).value = $event)),
                                                            type: "checkbox",
                                                            value: opt.value
                                                        }, null, 8, _hoisted_7), [
                                                            [_vModelCheckbox, selectedSpeakers.value]
                                                        ]),
                                                        _createElementVNode("label", {
                                                            for: `speaker-${opt.value}`,
                                                            textContent: _toDisplayString(__props.getDisplayName(opt.label))
                                                        }, null, 8, _hoisted_8)
                                                    ]));
                                                }), 128))
                                            ]),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true)
                                ]))
                                : _createCommentVNode("", true)
                        ], 2)),
                    (!isAltState.value && !__props.isHtmlTranscript)
                        ? (_openBlock(), _createBlock(AlertMessage, {
                            key: 3,
                            type: "info"
                        }, {
                            default: _withCtx(() => [
                                _createElementVNode("span", { innerHTML: alertMsg.value }, null, 8, _hoisted_9)
                            ]),
                            _: 1
                        }))
                        : _createCommentVNode("", true)
                ]),
                _renderSlot(_ctx.$slots, "before-messages"),
                (!isAltState.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        ref_key: "messagesContainer",
                        ref: messagesContainer,
                        class: "transcript-layout__messages"
                    }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(visibleItems), (entry, index, ___, _cached) => {
                            const _memo = (memoKeyFor(entry, index));
                            if (_cached && _cached.el && _cached.key === displayKey(entry, index) && _isMemoSame(_cached, _memo))
                                return _cached;
                            const _item = (_openBlock(), _createElementBlock(_Fragment, {
                                key: displayKey(entry, index)
                            }, [
                                (entry.type === 'message')
                                    ? _withDirectives((_openBlock(), _createBlock(TranscriptMessage, {
                                        key: 0,
                                        ref_for: true,
                                        ref: entry === targetEntry.value ? setMessageTarget : undefined,
                                        class: _normalizeClass({ emphasized: entry === targetEntry.value && _unref(isEmphasized) }),
                                        segments: entry.message.segments,
                                        "segment-end": segmentEndFor(index),
                                        "has-speaker-menu": hasSpeakerMenu(),
                                        "has-segment-menu": hasSegmentMenu(),
                                        "is-host": entry.message.isHost,
                                        "speaker-display-name": __props.getDisplayName(entry.message.speaker),
                                        "speaker-color": speakerColor(entry.message.speaker),
                                        "speaker-label": entry.message.speaker,
                                        "circular-avatar": __props.isKnownParticipant(entry.message.speaker),
                                        regenerating: isMessageRegenerating(entry.message.speaker),
                                        editable: __props.editable,
                                        "disable-speaker-menu-options": __props.disableSpeakerMenuOptions,
                                        "disable-text-hover": __props.disableTextHover,
                                        "disable-word-hover-underline": __props.disableWordHoverUnderline,
                                        "segment-text-attrs": __props.segmentTextAttrs,
                                        "focus-area-key": __props.focusAreaKey,
                                        "show-chat-affordances": __props.showChatAffordances,
                                        "speaker-inactive": __props.isSpeakerInactive(entry.message.speaker),
                                        "speaker-profile-url": __props.participants?.[entry.message.speaker ?? '']?.profileUrl,
                                        "speaker-profile-photo": __props.participants?.[entry.message.speaker ?? '']?.profilePhoto,
                                        onSeek: onSeek,
                                        onEditSegmentText: onEditSegmentText
                                    }, {
                                        "speaker-menu": _withCtx((slotProps) => [
                                            _renderSlot(_ctx.$slots, "speaker-menu", _mergeProps({ ref_for: true }, slotProps, {
                                                isBackroom: entry.message.isBackroom,
                                                channel: __props.participants?.[entry.message.speaker ?? '']?.channel
                                            }))
                                        ]),
                                        "segment-menu": _withCtx((slotProps) => [
                                            _renderSlot(_ctx.$slots, "segment-menu", _mergeProps({ ref_for: true }, slotProps))
                                        ]),
                                        "segment-decorations": _withCtx((slotProps) => [
                                            _renderSlot(_ctx.$slots, "segment-decorations", _mergeProps({ ref_for: true }, slotProps))
                                        ]),
                                        "word-decorations": _withCtx((slotProps) => [
                                            _renderSlot(_ctx.$slots, "word-decorations", _mergeProps({ ref_for: true }, slotProps))
                                        ]),
                                        "word-separator": _withCtx((slotProps) => [
                                            _renderSlot(_ctx.$slots, "word-separator", _mergeProps({ ref_for: true }, slotProps))
                                        ]),
                                        _: 2
                                    }, 1032, ["class", "segments", "segment-end", "has-speaker-menu", "has-segment-menu", "is-host", "speaker-display-name", "speaker-color", "speaker-label", "circular-avatar", "regenerating", "editable", "disable-speaker-menu-options", "disable-text-hover", "disable-word-hover-underline", "segment-text-attrs", "focus-area-key", "show-chat-affordances", "speaker-inactive", "speaker-profile-url", "speaker-profile-photo"])), [
                                        [_vShow, entry.visible]
                                    ])
                                    : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entry.rows, (row, rowIndex) => {
                                            return _renderSlot(_ctx.$slots, "interleaved-row", { row: row }, undefined, undefined, `${row.sortKey}-${rowIndex}`);
                                        }), 128))
                                    ]))
                            ], 64));
                            _item.memo = _memo;
                            return _item;
                        }, _cache, 2), 128))
                    ], 512))
                    : _createCommentVNode("", true),
                (!__props.isHtmlTranscript)
                    ? (_openBlock(), _createBlock(BackToTopButton, { key: 1 }))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
